import { openEnquiry, scrollTo } from "../../lib/api";
import { Reveal } from "../Reveal";

const LOANS = [
  ["Personal loan", "Planned expenses without putting anything up as security.", "Usually none.", "Rate depends on your credit profile. Compare total cost, not just the EMI.", "personal_loan"],
  ["Business loan", "Working capital, stock, equipment and growth.", "Unsecured or secured, depending on the lender.", "Lenders look closely at cash flow, bank statements, ITR and GST.", "business_loan"],
  ["Home loan", "Buying, building or renovating a home.", "The property itself.", "Legal and technical checks take time. Tenures are the longest.", "home_loan"],
  ["Vehicle loan", "Buying a new or used vehicle.", "The vehicle.", "Check the down payment, insurance and foreclosure terms.", "vehicle_loan"],
  ["Loan against property", "Larger funds using a property you own.", "The property.", "If repayments fail, the property is at risk. Borrow only what you can service.", "loan_against_property"],
  ["MSME, Mudra and PMEGP", "Small and new businesses using government-backed schemes.", "Depends on the scheme and the lender.", "Scheme limits, subsidies and rules change. We check the current terms for you.", "msme_mudra_pmegp"],
];

export function Services({ settings }) {
  const insurance = settings?.flags?.show_insurance;
  const cards = [
    ["01", "Loans", "Personal, business, home, vehicle, loan against property and MSME, Mudra and PMEGP schemes.", "Check my options", () => openEnquiry({})],
    ["02", "GST, ITR and ROC", "Registrations, returns and compliance filings, done with documents you can track.", "Book tax help", () => scrollTo("tax")],
  ];
  if (insurance) cards.push(["03", "Insurance", "Talk through cover options with a specialist before you buy anything.", "Talk to us", () => scrollTo("contact")]);
  return (
    <section id="services" className="py-16 md:py-24">
      <div className="max-w-[1200px] mx-auto px-4 md:px-8">
        <Reveal><p className="text-xs uppercase tracking-[0.2em] font-extrabold text-[#f06400]">What we help with</p>
          <h2 className="mt-2 text-3xl md:text-4xl font-extrabold tracking-[-0.035em]">Services.</h2></Reveal>
        <div className="mt-8 grid md:grid-cols-3 gap-5">
          {cards.map((c, i) => (
            <Reveal key={c[0]} delay={i * 90}>
              <div className={`rounded-[28px] p-7 h-full flex flex-col ${i === 0 ? "bg-[#191c1f] text-white" : i === 1 ? "bg-[#e2f0ef]" : "bg-[#fff0e6]"}`}>
                <div className="text-sm font-extrabold opacity-60">{c[0]}</div>
                <h3 className="mt-2 text-2xl font-extrabold">{c[1]}</h3>
                <p className={`mt-2 text-sm flex-1 ${i === 0 ? "text-white/75" : "text-[#191c1f]/75"}`}>{c[2]}</p>
                <button onClick={c[4]} data-testid={`service-cta-${i}`}
                  className={`mt-4 self-start min-h-[44px] px-5 rounded-full font-bold ${i === 0 ? "bg-white text-[#191c1f]" : "bg-[#191c1f] text-white"}`}>{c[3]}</button>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

export function Compare({ settings }) {
  const rates = settings?.rates || [];
  const basis = settings?.rate_basis;
  const basisLabel = basis === "reducing" ? " (reducing balance)" : basis === "flat" ? " (flat)" : "";
  return (
    <section id="compare" className="bg-[#f4f5f6] py-16 md:py-24">
      <div className="max-w-[1200px] mx-auto px-4 md:px-8">
        <Reveal><p className="text-xs uppercase tracking-[0.2em] font-extrabold text-[#f06400]">Compare loans</p>
          <h2 className="mt-2 text-3xl md:text-4xl font-extrabold tracking-[-0.035em]">Know what you're getting into.</h2></Reveal>
        <div className="mt-8 grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {LOANS.map((l, i) => (
            <Reveal key={l[0]} delay={i * 60}>
              <div className="rounded-[28px] bg-white border border-[#e1e2e5] p-6 h-full flex flex-col">
                <h3 className="text-xl font-extrabold">{l[0]}</h3>
                <dl className="mt-3 space-y-2 text-sm flex-1">
                  <div><dt className="font-bold text-[#6b6f76]">Best for</dt><dd>{l[1]}</dd></div>
                  <div><dt className="font-bold text-[#6b6f76]">Security</dt><dd>{l[2]}</dd></div>
                  <div><dt className="font-bold text-[#6b6f76]">Keep in mind</dt><dd>{l[3]}</dd></div>
                </dl>
                <button onClick={() => openEnquiry({ product: l[4] })} data-testid={`compare-ask-${l[4]}`}
                  className="mt-4 self-start min-h-[44px] px-5 rounded-full border-2 border-[#191c1f] font-bold">Ask about this</button>
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal className="mt-12">
          <div className="rounded-[28px] bg-white border border-[#e1e2e5] p-6 md:p-8">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <h3 className="text-2xl font-extrabold tracking-[-0.03em]">Indicative interest rates</h3>
              <button onClick={() => scrollTo("tools")} data-testid="open-converter"
                className="min-h-[44px] px-5 rounded-full bg-[#f06400] text-[#191c1f] font-extrabold">Open the flat vs reducing converter</button>
            </div>
            <div className="mt-5 overflow-x-auto">
              <table className="w-full text-left" data-testid="rates-table">
                <thead><tr className="border-b border-[#e1e2e5]">
                  <th className="py-3 text-sm uppercase tracking-wide text-[#6b6f76]">Loan</th>
                  <th className="py-3 text-sm uppercase tracking-wide text-[#6b6f76]">Interest rate (per year){basisLabel}</th>
                </tr></thead>
                <tbody>
                  {rates.map((r) => (
                    <tr key={r.loan} className="border-b border-[#f0f1f2]">
                      <td className="py-3 font-bold">{r.loan}</td>
                      <td className="py-3 font-extrabold text-[#004040]">{r.rate}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="mt-4 text-sm text-[#6b6f76]">{settings?.rates_note}</p>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

const STEPS = [["1", "Tell us", "Pick what you need and an amount. Three fields, under a minute."],
  ["2", "Verify", "Confirm your mobile and your consent to be contacted."],
  ["3", "Match", "We line up partner lenders that fit, with terms shown by the lender."],
  ["4", "Decide", "A specialist walks you through documents and status. You choose."]];

export function HowItWorks() {
  return (
    <section id="how" className="py-16 md:py-24">
      <div className="max-w-[1200px] mx-auto px-4 md:px-8">
        <Reveal><p className="text-xs uppercase tracking-[0.2em] font-extrabold text-[#f06400]">How it works</p>
          <h2 className="mt-2 text-3xl md:text-4xl font-extrabold tracking-[-0.035em]">Four steps, no surprises.</h2></Reveal>
        <div className="mt-8 grid md:grid-cols-4 gap-5">
          {STEPS.map((s, i) => (
            <Reveal key={s[0]} delay={i * 80}>
              <div className="rounded-[28px] border border-[#e1e2e5] p-6 h-full bg-white">
                <div className="w-10 h-10 rounded-full bg-[#191c1f] text-white grid place-items-center font-extrabold">{s[0]}</div>
                <h3 className="mt-3 text-xl font-extrabold">{s[1]}</h3>
                <p className="mt-2 text-sm text-[#191c1f]/75">{s[2]}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

export function About({ settings }) {
  const c = settings?.contact || {};
  return (
    <>
      <section id="about" className="bg-[#f4f5f6] py-16 md:py-24">
        <div className="max-w-[1200px] mx-auto px-4 md:px-8 grid lg:grid-cols-2 gap-10">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.2em] font-extrabold text-[#f06400]">About us</p>
            <h2 className="mt-2 text-3xl md:text-4xl font-extrabold tracking-[-0.035em]">Built on trust, since 2017.</h2>
            <p className="mt-4 text-[#191c1f]/75 max-w-xl">Bhartiya Loan Capital is operated by Bhartiya Capital Advisory &amp; Consulting LLP, a Limited Liability Partnership. We help people and businesses find the right loan with the right lender — clearly and fairly.</p>
          </Reveal>
          <Reveal delay={120}>
            <ol className="relative border-l-2 border-[#e1e2e5] pl-6 space-y-6">
              {[["16 Oct 2017", "One Person Company begins", "The business started serving customers as a One Person Company."],
                ["2 Jul 2026", "Upgraded to an LLP", "Became Bhartiya Capital Advisory & Consulting LLP (LLPIN ACZ-6631)."],
                ["Today", "Loans, tools and guidance", "A facilitation service, free tools, and optional consulting plans."]].map((t) => (
                <li key={t[0]} className="relative">
                  <span className="absolute -left-[31px] top-1 w-4 h-4 rounded-full bg-[#f06400] border-4 border-[#f4f5f6]" />
                  <div className="text-sm font-extrabold text-[#004040]">{t[0]}</div>
                  <div className="font-extrabold">{t[1]}</div>
                  <p className="text-sm text-[#191c1f]/70">{t[2]}</p>
                </li>
              ))}
            </ol>
          </Reveal>
        </div>
      </section>

      <section id="founder" className="py-16 md:py-24">
        <div className="max-w-[1200px] mx-auto px-4 md:px-8 grid lg:grid-cols-2 gap-10 items-center">
          <Reveal>
            <div className="rounded-[28px] bg-[#191c1f] text-white p-10">
              <div className="text-xs uppercase tracking-[0.25em] text-[#f06400] font-extrabold">Founder</div>
              <div className="mt-3 text-4xl md:text-5xl font-extrabold tracking-[-0.04em] leading-none">Mr. Vivek<br />Tusham</div>
              <div className="mt-4 text-white/70 font-bold">Founder &amp; Executive Manager</div>
              <a href={c.linkedin} target="_blank" rel="noopener" data-testid="founder-linkedin"
                className="mt-5 inline-block min-h-[44px] px-5 rounded-full bg-white text-[#191c1f] font-bold leading-[44px]">LinkedIn →</a>
            </div>
          </Reveal>
          <Reveal delay={120}>
            <div className="grid sm:grid-cols-3 gap-3">
              {[["Customer first", "Tailored guidance instead of one-size-fits-all products."],
                ["Clear and fair", "A simple process with transparent steps and honest answers."],
                ["Better with technology", "Smarter tools and data to make service faster and more reliable."]].map((p) => (
                <div key={p[0]} className="rounded-[24px] border border-[#e1e2e5] p-5">
                  <h3 className="font-extrabold">{p[0]}</h3>
                  <p className="mt-2 text-sm text-[#191c1f]/70">{p[1]}</p>
                </div>
              ))}
            </div>
            <div className="mt-4 grid sm:grid-cols-3 gap-3 text-sm">
              <div className="rounded-2xl bg-[#f4f5f6] p-4"><div className="font-bold text-[#6b6f76]">Role</div>Founder &amp; Executive Manager</div>
              <div className="rounded-2xl bg-[#f4f5f6] p-4"><div className="font-bold text-[#6b6f76]">Education</div>Delhi University</div>
              <div className="rounded-2xl bg-[#f4f5f6] p-4"><div className="font-bold text-[#6b6f76]">Based in</div>Delhi</div>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}

export function DoDont() {
  const doo = ["Explain your options in plain language.", "Match you with partner lenders that fit.", "Show the lender's terms before you accept.", "Use your data only for your enquiry, with your consent."];
  const dont = ["Charge you to apply for a loan.", "Promise or guarantee approval.", "Ask for your OTP, PIN or passwords.", "Pressure you to take an offer."];
  return (
    <section className="py-10">
      <div className="max-w-[1200px] mx-auto px-4 md:px-8 grid md:grid-cols-2 gap-5">
        <Reveal><div className="rounded-[28px] bg-[#e2f0ef] p-7 h-full">
          <h3 className="text-2xl font-extrabold">What we do</h3>
          <ul className="mt-4 space-y-2">{doo.map((x) => <li key={x} className="flex gap-2 text-sm"><span className="text-[#004040] font-extrabold">✓</span>{x}</li>)}</ul>
        </div></Reveal>
        <Reveal delay={100}><div className="rounded-[28px] bg-[#fff0e6] p-7 h-full">
          <h3 className="text-2xl font-extrabold">What we never do</h3>
          <ul className="mt-4 space-y-2">{dont.map((x) => <li key={x} className="flex gap-2 text-sm"><span className="text-[#b84a00] font-extrabold">✕</span>{x}</li>)}</ul>
        </div></Reveal>
      </div>
    </section>
  );
}

const LIC = [
  ["Who we are", "Bhartiya Loan Capital is a digital platform that helps you compare loan options and connect with lending partners.", "bg-white border border-[#e1e2e5]"],
  ["Not a lender", "We do not lend money or take credit decisions. All approvals, rates and terms are decided by the lending partner.", "bg-white border border-[#e1e2e5]"],
  ["Business entity", "Operated by Bhartiya Capital Advisory & Consulting LLP (LLPIN ACZ-6631).", "bg-[#e2f0ef]"],
  ["Stay safe", "We never ask for an upfront fee, OTP, PIN or password. Report suspicious activity to cybercrime.gov.in.", "bg-[#fff0e6]"],
];

export function Licences() {
  return (
    <section id="licences" className="bg-[#f4f5f6] py-16 md:py-24">
      <div className="max-w-[1200px] mx-auto px-4 md:px-8">
        <Reveal><p className="text-xs uppercase tracking-[0.2em] font-extrabold text-[#f06400]">Disclosures</p>
          <h2 className="mt-2 text-3xl md:text-4xl font-extrabold tracking-[-0.035em]">Clear about who we are.</h2></Reveal>
        <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {LIC.map(([t, b, cls], i) => (
            <Reveal key={t} delay={i * 70}>
              <div className={`rounded-[28px] p-6 h-full ${cls}`} data-testid={`disclosure-card-${i}`}>
                <h3 className="text-xl font-extrabold">{t}</h3>
                <p className="mt-3 text-sm text-[#191c1f]/80">{b}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

export function FinalCta() {
  return (
    <section id="contact" className="py-16">
      <div className="max-w-[1200px] mx-auto px-4 md:px-8">
        <Reveal><div className="rounded-[32px] bg-[#191c1f] text-white p-10 md:p-16 text-center">
          <h2 className="text-3xl md:text-5xl font-extrabold tracking-[-0.035em]">Ready when you are.</h2>
          <p className="mt-4 text-white/70 max-w-xl mx-auto">Tell us what you need. Free to apply, clear steps, real people.</p>
          <div className="mt-6 flex flex-wrap gap-3 justify-center">
            <button onClick={() => openEnquiry({})} data-testid="final-cta-check" className="min-h-[48px] px-7 rounded-full bg-[#f06400] text-[#191c1f] font-extrabold">Check my options</button>
            <a href="tel:+917042249097" className="min-h-[48px] px-7 rounded-full border-2 border-white font-bold grid place-items-center">Call +91 70422 49097</a>
          </div>
        </div></Reveal>
      </div>
    </section>
  );
}
